// The five things a club can say about an event beyond what and when.
//
// Four of them are badges and one is a button, but they are one feature: a
// club ticking a box in the creator, and every screen that shows an event
// saying the same thing about it. They live here rather than in each screen
// because there are several of those — the creator's own card, the campus map's
// sheet and calendar, the attendee page and Events Admin — and a tag that reads
// differently on two of them is a tag nobody trusts.
//
//  · FREE FOOD is a plain boolean and the most-read fact on this whole list.
//  · FACULTY EXCLUSIVE is a list of `src/utils/faculties.js` ids. Empty means
//    everybody, which is the normal case and must never print a badge.
//    ⚠️ NOTHING FILTERS ON IT — it is printed, on every surface including the
//    phone. The phone app is the only one that COULD (it asked during
//    onboarding and knows who is looking) and deliberately does not, which is
//    also why this file is worth mirroring there rather than re-deciding: an
//    attendee reads "Engineering students only" and knows before they walk
//    over, and a Science student is entitled to decide an Engineering social is
//    worth turning up to anyway. An event that is silently absent cannot be
//    argued with. Don't add hiding on one client without the other — a
//    restriction that hides here and prints there is two different events.
//  · TICKETS is a link and a price, and it renders as the one button on an
//    event that is not about getting there.
//  · CUSTOM TAGS are up to three short phrases the club writes itself, printed
//    verbatim. ⚠️ THIS IS THE ONLY TAG WHOSE WORDS WE DO NOT CHOOSE, which is
//    the whole point of it and the whole risk in it: "Bring a mug", "Costumes
//    welcome" and "Cash only" are facts no fixed vocabulary was ever going to
//    cover, and a club that cannot say them ends up putting them in the event's
//    name. What keeps that safe is that they are SHORT, FEW and QUIET — see
//    `normalizeCustomTags` for the rules and `EventTags.jsx` for the tone.
//    They replaced ATTENDEE SIGNUPS, a number the club typed on its dashboard
//    and which every screen printed as "150 signed up". Nothing on this site
//    counts anybody, so that badge was always the club's claim about a list we
//    could not see, it went stale the moment the club stopped retyping it, and
//    it took a whole editable box on every card to keep it honest. Three words
//    a club chose are worth more than one number it had to maintain.
//    ⚠️ Events already stored carry an `attendees` number. It is deliberately
//    NOT migrated and NOT purged — nothing reads or writes it any more, so it
//    sits in KV as dead weight rather than as a migration that could go wrong
//    against live data for a field whose whole future is being ignored.
//  · OFF CAMPUS is not really a tag at all — it replaces the location. An
//    off-campus event carries no building, so it gets no pin on the campus map
//    (there is nowhere honest to put one) and no walking route; what it has
//    instead is an address and a link out to Google Maps.
import { facultyShort, isFaculty } from './faculties.js';

/** Is this event somewhere the campus map cannot draw? */
export const isOffCampus = (e) => !!e?.offCampus;

/** The address a club typed, or '' — an off-campus event may be half-made. */
export const offCampusAddress = (e) => String(e?.offCampus?.address || '').trim();

/**
 * Where to send somebody for an off-campus event.
 *
 * Normally a Google Maps search for the address, which is what "a Google Maps
 * address" means and what resolves a half-remembered one ("Forks Market"). A
 * club that pastes a maps link instead gets that link used as-is: it is more
 * precise than anything a search can do with it, and searching Maps FOR a Maps
 * URL returns nothing at all.
 */
export function offCampusLink(address) {
  const s = String(address || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s)}`;
}

/** A pasted link is a destination, not something to print as an address. */
export const isLinkAddress = (address) => /^https?:\/\//i.test(String(address || '').trim());

/**
 * The location line: "Drake Centre · Room 204", or an off-campus address.
 *
 * A PINNED spot has no room number and says whatever the club called it — "Main
 * atrium, by the stairs" — falling back to the building alone, which is still
 * true, rather than to "Pinned spot", which tells an attendee nothing.
 */
export function eventWhere(e) {
  if (isOffCampus(e)) {
    const addr = offCampusAddress(e);
    return addr && !isLinkAddress(addr) ? `Off campus · ${addr}` : 'Off campus';
  }
  return [
    e?.buildingName,
    e?.room?.roomNumber ? `Room ${e.room.roomNumber}`
      : (e?.room?.point && e?.room?.label && e.room.label !== 'Pinned spot') ? e.room.label : '',
  ].filter(Boolean).join(' · ');
}

/** The faculty ids this build still recognises, in the list's own order. */
export function facultiesOf(e) {
  return (Array.isArray(e?.faculties) ? e.faculties : []).filter(isFaculty);
}

/**
 * "Engineering students only" — who an exclusive event is for.
 *
 * Named one by one up to two, because those are the names somebody checks
 * themselves against. Past that the list is longer than the badge and the
 * count is the more useful reading; the full list rides on the `title` for
 * anyone who wants it.
 */
export function facultyTag(e) {
  const ids = facultiesOf(e);
  if (!ids.length) return null;
  const names = ids.map(facultyShort);
  const label = names.length === 1 ? `${names[0]} students only`
    : names.length === 2 ? `${names[0]} & ${names[1]} students only`
      : `${names.length} faculties only`;
  return { label, title: names.join(', ') };
}

/**
 * A price, as it is printed.
 *
 * Zero is a real answer and not a missing one: a ticketed event can be free,
 * and plenty are — a club uses the ticket to count who is coming. Whole
 * dollars lose the cents, because "$10.00" on a poster reads as a form field.
 */
export function priceLabel(price) {
  // ⚠️ `Number('')` is 0, and 0 means free — so an empty box would price every
  // event at nothing. Blanks are refused before the conversion, not after it.
  if (price == null || price === '') return '';
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  if (n <= 0) return 'Free';
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/**
 * The tickets button: where it goes and what it says.
 *
 * The link is what makes the button, not the price — a club that has set up
 * ticketing and not typed a number still has somewhere to send people, and a
 * price with nowhere to buy is a button that would do nothing.
 */
export function ticketOf(e) {
  const url = String(e?.tickets?.url || '').trim();
  if (!/^https?:\/\//i.test(url)) return null;
  const price = priceLabel(e?.tickets?.price);
  return {
    url,
    label: !price ? 'Buy tickets' : price === 'Free' ? 'Get free tickets' : `Buy tickets: ${price}`,
    price,
  };
}

/** At most three, at most fifteen characters each — see below for both. */
export const MAX_CUSTOM_TAGS = 3;
export const MAX_CUSTOM_TAG_CHARS = 15;

// By code point rather than by a `[\x00-\x1f]` class, which is exactly what
// `no-control-regex` exists to stop and which would need the rule switched off
// in the one file where the rule is right to be suspicious. Spread rather than
// indexed, so an emoji is one character here and one character in the counter
// the club is watching.
const chars = (s) => Array.from(s);
const stripControls = (s) => chars(s).filter(c => c >= ' ' && c !== '\x7f').join('');

/**
 * The club's own tags, cleaned: trimmed, stripped, capped, de-duplicated.
 *
 * ⚠️ ONE IMPLEMENTATION, TWO COPIES, NO THIRD OPINION. Everything else on an
 * event is chosen from a list we wrote; this is free text going onto a public
 * campus map, so the store and the client agreeing about what is acceptable is
 * the whole safety model. The server's copy lives in `api/_eventStore.js`
 * (CommonJS cannot require this ESM file) and must be changed with this one —
 * the same arrangement `FACULTY_IDS` documents there. A client that is stricter
 * than the store lets a hand-written POST through; a client that is looser
 * shows a club a tag that silently vanishes on save.
 *
 * The rules, and why each one:
 *  · **Control characters are stripped** before anything else. These are printed
 *    into a badge on five screens and into a phone app; a stray `\x00` or a
 *    newline is invisible in the box the club typed it into and wrong-looking
 *    everywhere it lands.
 *  · **Trimmed**, then **empties dropped** — an Add button pressed on a blank
 *    box, or a tag cleared back to spaces, is not a tag.
 *  · **Over 15 characters is REFUSED, never truncated.** Truncation publishes
 *    something the club did not write and cannot see it did not write: "Bring
 *    your own mug" becoming "Bring your own" reads as a finished phrase. A
 *    refusal is visible at the point of typing — the counter runs out and Add
 *    does nothing — so the club shortens it in its own words. Fifteen is about
 *    two words, which is what fits in a pill beside "Free food" on a card three
 *    hundred pixels wide.
 *  · **At most 3**, and the 4th onward are dropped rather than the list being
 *    rejected. A badge row is read at a glance; six club-authored pills next to
 *    the two that mean something is how the two that mean something stop being
 *    read.
 *  · **Duplicates fold case-insensitively**, first spelling kept: "Cash only"
 *    and "cash only" are one tag printed twice, which reads as a mistake on the
 *    club's part and spends one of its three.
 */
export function normalizeCustomTags(raw) {
  const out = [];
  const seen = new Set();
  for (const v of Array.isArray(raw) ? raw : []) {
    if (typeof v !== 'string') continue;
    const s = stripControls(v).trim();
    if (!s || chars(s).length > MAX_CUSTOM_TAG_CHARS) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length === MAX_CUSTOM_TAGS) break;
  }
  return out;
}

/** The tags this event actually carries, cleaned — what every screen draws. */
export const customTagsOf = (e) => normalizeCustomTags(e?.customTags);

/**
 * Every badge an event carries, in reading order.
 *
 * Free food first: it is the one that changes whether somebody walks over.
 * `key` doubles as a CSS modifier, so a new tag is a line here and a colour in
 * the stylesheet.
 *
 * Off campus is deliberately NOT one of these. It is already the first thing
 * `eventWhere` says, on every screen that shows a badge row, and a badge
 * repeating the location line reads as two different facts until you have read
 * both. Tickets are not one either — they are a button, and a badge saying
 * "tickets" next to a button saying "Buy tickets: $10" is the same trade.
 */
export function eventTags(e) {
  const out = [];
  if (e?.freeFood) out.push({ key: 'food', label: 'Free food' });
  const faculty = facultyTag(e);
  if (faculty) out.push({ key: 'faculty', label: faculty.label, title: faculty.title });
  // ⚠️ THE CLUB'S OWN WORDS COME LAST, always, and they all share one key.
  //
  // Last because the two above are facts this site understands and can be held
  // to — an attendee who sees "Free food" and finds none has been misled by us.
  // A custom tag is a sentence the club wrote, which we print and do not vouch
  // for; leading with it would put the one unvetted thing on the row where the
  // eye lands first. Ordering them after `faculty` is also what stops a club
  // from pushing a restriction somebody needs to read off the end of a narrow
  // badge row by adding three tags.
  //
  // `key` is 'custom' for every one of them rather than 'custom-0', 'custom-1':
  // it doubles as the CSS modifier, and three pills in three different tones
  // would read as three kinds of thing. React keys are the renderer's problem
  // (see EventTags.jsx), not this vocabulary's.
  for (const label of customTagsOf(e)) out.push({ key: 'custom', label });
  return out;
}
